/**
 * QuickstartMobileThankYou
 *
 * Mobile-specific thank you page that shows instructions for adding the app to home screen.
 * Displays different instructions based on device (iOS vs Android).
 * This is shown after the demo ends on mobile devices.
 */

import { useCallback, useEffect, useRef } from 'react';
import { QuickstartCard } from './QuickstartCard';
import { QuickstartTheme, getWishlistButtonImage } from '../types/theme';
import { buildQuickstartSteamUrl } from '../utils/steamUrls';

interface QuickstartMobileThankYouProps {
  theme: QuickstartTheme;
  autoRedirect?: boolean;
  onRedirect?: (steamUrl: string) => void;
}

export const QuickstartMobileThankYou = ({ theme, autoRedirect, onRedirect }: QuickstartMobileThankYouProps) => {
  const hasAutoRedirected = useRef(false);

  const handleCheckItOut = useCallback(() => {
    if (!theme.steamUrl) {
      return;
    }

    const finalUrl = buildQuickstartSteamUrl(theme.steamUrl);

    // Send analytics callback
    if (onRedirect) {
      onRedirect(finalUrl);
    }

    // Open in new tab instead of redirecting (prevents canceling analytics request)
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  }, [onRedirect, theme.steamUrl]);

  useEffect(() => {
    if (!autoRedirect || hasAutoRedirected.current) {
      return;
    }

    if (!theme.steamUrl) {
      return;
    }

    hasAutoRedirected.current = true;

    // Auto-redirect: Use location.href instead of window.open to avoid popup blockers
    const finalUrl = buildQuickstartSteamUrl(theme.steamUrl);

    // Send analytics callback before redirect
    if (onRedirect) {
      onRedirect(finalUrl);
    }

    // Delay to ensure analytics can be sent
    setTimeout(() => {
      window.location.href = finalUrl;
    }, 500);
  }, [autoRedirect, onRedirect, theme.steamUrl]);


  return (
    <div
      className='flex min-h-screen items-center justify-center overflow-auto p-4'
      style={{
        backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <QuickstartCard theme={theme} showHeader={false}>
        <div className='flex flex-col items-center text-center'>
          {/* Studio Logo */}
          {theme.studioLogo && <img src={theme.studioLogo} alt={theme.studioName} className='mt-4 h-16' />}

          {/* Game Logo */}
          {theme.gameLogo && <img src={theme.gameLogo} alt={theme.gameName} className='mt-8 h-32' />}

          {/* Add to Home Screen Text */}
          <div className='mt-8 font-["Inter"] text-2xl font-semibold' style={{ color: theme.primaryColor }}>
            Add to your home screen!
          </div>

          {/* Add to Home Screen GIF - Placeholder */}
          <div className="mt-6 w-full max-w-sm">
            <img
              src="/quickstart/phone-ad-3.gif"
              alt="Add to home screen instructions"
              className="w-full h-auto"
            />
          </div>

          {/* Wishlist/Store Button */}
          {theme.steamUrl && (() => {
            const buttonImage = getWishlistButtonImage(theme);
            return (
              <img
                src={buttonImage.src}
                onClick={handleCheckItOut}
                className='mt-6 h-16 cursor-pointer transition-opacity hover:opacity-80'
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCheckItOut();
                  }
                }}
                alt={buttonImage.alt}
              />
            );
          })()}
        </div>
      </QuickstartCard>
    </div>
  );
};
